import React from 'react';
import { useParams, Link } from 'react-router';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, Share2, Bookmark } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import SEO, { createArticleJsonLd } from '../../components/seo/SEO';
import { blogPostsBySlug } from '../../data/blogPosts';

export default function BlogDetailScreen(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPostsBySlug[slug || ''];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Article not found</h2>
          <p className="text-slate-500 mb-4">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-amber-500 font-semibold hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.image}
        type="article"
        canonicalPath={`/blog/${post.slug}`}
        jsonLd={createArticleJsonLd(post)}
      />
      {/* Hero Image */}
      <div className="h-64 sm:h-80 lg:h-96 overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" itemProp="image" />
      </div>

      <article className="max-w-3xl mx-auto px-4 py-12" itemScope itemType="https://schema.org/BlogPosting">
        {/* Back link */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-500 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </motion.div>

        {/* Category */}
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-bold mb-4"
        >
          {post.category}
        </motion.span>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight"
          itemProp="headline"
        >
          {post.title}
        </motion.h1>

        {/* Meta */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-4 mb-8 pb-8 border-b border-slate-100"
        >
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <User className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900" itemProp="author">{post.author}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <time itemProp="datePublished" dateTime={post.date} className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</time>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
              </div>
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <button className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
              <Share2 className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
              <Bookmark className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-p:text-slate-600 prose-p:leading-relaxed prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4"
          dangerouslySetInnerHTML={{ __html: post.content }}
          itemProp="articleBody"
        />

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 p-6 bg-amber-50 rounded-2xl border border-amber-100"
        >
          <h3 className="text-lg font-bold text-slate-900 mb-2">Want to master AI for Finance?</h3>
          <p className="text-sm text-slate-600 mb-4">
            Join our 12-week live cohort and build real BFSI projects with local AI.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors"
          >
            Explore Courses <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
        </motion.div>
      </article>
      <Footer />
    </div>
  );
}
